import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useStore } from '../../../StateProvider'
import { InputMasked } from '../../../ui'
import { map } from 'ramda'
import { usePresetValue } from '../../utils/hooks'
import { convertToClientDate, convertToServerDate } from '../../utils/utils'
import { getUserValue } from '../utils'
import { RTL_LANGUAGES } from '../../../constants/constants'

/**
 * @param {Date} d
 * @returns {boolean}
 */
const isValidDate = (d) => d instanceof Date && !Number.isNaN(d.getTime())

/**
 * @param {string} value
 * @returns {boolean}
 */
const isValidBirth = (value) => {
  const date = new Date(convertToServerDate(value))
  return value.indexOf('_') === -1 && isValidDate(date)
}

/**
 * @param {string} value
 * @returns {boolean}
 */
const isValidAge = (value) => {
  const date = new Date(convertToServerDate(value))
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return date <= getAgeMax() && date >= getAgeMin()
}

/**
 * @returns {Date}
 */
const getAgeMin = () => {
  const today = new Date()
  return new Date(today.getFullYear() - 120, today.getMonth(), 1)
}

/**
 * @returns {Date}
 */
const getAgeMax = () => {
  const today = new Date()
  return new Date(today.getFullYear() - 18, today.getMonth(), today.getDate() + 1)
}

/**
 * @param {string} value
 * @returns {string}
 */
const getAgeErrorMessage = (value) => {
  const date = new Date(convertToServerDate(value))
  return date < getAgeMin() ? 'generatedField.incorrectValue' : 'generatedField.ageInfo'
}

const ExternalDateField = ({ i18nLabel, type, name }) => {
  const { i18n, t } = useTranslation()
  const label = i18nLabel ? t(`generatedField.${i18nLabel}`) : ''
  const [isValid, setIsValid] = useState(null)
  const [isFilled, setIsFilled] = useState(false)
  const [error, setError] = useState('')
  const [{ user, form: { accId } }, dispatch] = useStore()
  const [value, setValue] = useState('')
  const userValue = getUserValue(accId, user, name)

  const presetValue = (value) => {
    const convertedValue = convertToClientDate(value)
    const isValidFormat = isValidBirth(convertedValue)
    const isValidAgeRange = isValidAge(convertedValue)
    const isValid = isValidFormat && isValidAgeRange
    const validationError = !isValidFormat ? 'generatedField.birthInfo' : getAgeErrorMessage(convertedValue)
    setIsValid(isValid)
    setValue(convertedValue)
    setError(!isFilled || isValid ? '' : validationError)
    dispatch({ type: 'setExtFieldValue', name, value })
    dispatch({ type: 'setValidationFld', name, value: isValid })
  }

  usePresetValue(name, userValue, presetValue)

  const onChange = ({ target: { value } }) => {
    setValue(value)
    !value.trim() ? setIsFilled(false) : setIsFilled(true)
  }

  const onBlur = ({ target: { value } }) => {
    const isValidFormat = isValidBirth(value)
    const isValidAgeRange = isValidAge(value)
    const isValid = isValidFormat && isValidAgeRange

    if (isValid) {
      setIsValid(true)
      setError('')
      dispatch({
        type: 'setExtFieldValue',
        name,
        value: convertToServerDate(value)
      })
    } else {
      setIsValid(isValid)
      const validationError = !isValidFormat ? 'generatedField.birthInfo' : getAgeErrorMessage(value)
      setError(!isFilled ? '' : validationError)
    }

    dispatch({
      type: 'setValidationFld',
      name,
      value: isValid
    })
  }

  return (
    <InputMasked
      name={name}
      label={label}
      value={value}
      key={name}
      onBlur={onBlur}
      onChange={onChange}
      valid={isValid}
      error={t(error)}
      type='text'
      mask={[/[0-9]/, /\d/, '.', /\d/, /\d/, '.', /\d/, /\d/, /\d/, /\d/]}
      placeholder={RTL_LANGUAGES.includes(i18n?.language) ? 'YYYY.MM.DD' : 'DD.MM.YYYY'}
    />
  )
}

const getExternalDateFields = map(dateField => {
  const { i18n, name, required, type, order } = dateField
  return (
    <ExternalDateField
      i18nLabel={i18n}
      name={name}
      required={required}
      type={type}
      key={name}
      order={order}
    />
  )
})

export { ExternalDateField, isValidBirth, isValidAge, isValidDate, getExternalDateFields }
